import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Checkbox, IconButton, TextField, Tooltip } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import CheckIcon from '@material-ui/icons/Check';
import ClearIcon from '@material-ui/icons/Clear';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import { useStylesForSettings } from '../../../components/SettingsComponents/settingsHelpers';
import SettingsTableHeader from '../../../components/SettingsComponents/SettingsTableHeader';
import { useHardwareMetaData } from '../../../hooks/useHardwareMetaData';
import { checkboxStyle } from '../../HardwareList/CheckBoxHandler';
import { AddOrganizationFormValue } from './addOrganization';
import { emptyOrganization, UpdateOrganization } from './updateOrganization';
import {
  addOrganizationDtoInitialValues,
  useAddOrganizationData,
} from './useAddOrganizationData';
import { useDeleteOrganizationData } from './useDeleteOrganizationData';
import { useUpdateOrganizationData } from './useUpdateOrganizationData';

const organizationColumns = [
  { id: 'name', numeric: false, disablePadding: true, label: 'name' },
  {
    id: 'noHardwareMaintenance',
    numeric: false,
    disablePadding: true,
    label: 'no hardware maintenance',
  },
  { id: 'action', numeric: false, disablePadding: true, label: '' },
];

const OrganizationsList = () => {
  const classes = useStylesForSettings();
  const data = useHardwareMetaData();
  const { submitDeleteOrganization } = useDeleteOrganizationData();
  const { submitAddOrganization } = useAddOrganizationData();
  const { submitUpdateOrganization } = useUpdateOrganizationData();
  const [selected, setSelected] = React.useState(-1);
  const [values, setValues] = React.useState<AddOrganizationFormValue>(
    addOrganizationDtoInitialValues,
  );
  const [organizations, setOrganizations] = useState(data.organizations);

  const [updatedValues, setUpdatedValues] =
    React.useState<UpdateOrganization>(emptyOrganization);

  const handleCreateChange =
    (name: keyof AddOrganizationFormValue, inputProperty: keyof HTMLInputElement) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setValues({ ...values, [name]: event.target[inputProperty] });
    };

  const handleSubmit = async (event: React.FormEvent<HTMLButtonElement>) => {
    try {
      const res = await submitAddOrganization(values);
      if (res) {
        setOrganizations([
          ...organizations,
          res as { id: number; noHardwareMaintenance: boolean; name: string },
        ]);
        setValues(addOrganizationDtoInitialValues);
      }
    } catch (e: any) {
      toast.error(e.message);
    }
    event.preventDefault();
  };

  const handleUpdate = async (id: number) => {
    try {
      const resType = await submitUpdateOrganization(id, updatedValues);
      if (resType === 'success') {
        setOrganizations((prevOrganizations) =>
          prevOrganizations.map((org) =>
            org.id === id
              ? {
                  ...org,
                  name: updatedValues.name,
                  noHardwareMaintenance: updatedValues.noHardwareMaintenance,
                }
              : org,
          ),
        );
      }

      deactivateEditMode();
    } catch {
      toast.error('Error updating organization');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const resType = await submitDeleteOrganization(id);
      if (resType === 'success') {
        setOrganizations((prevOrganizations) =>
          prevOrganizations.filter((org) => org.id !== id),
        );
      }
    } catch {
      toast.error('Error deleting organization');
    }
  };

  const handleUpdateChange =
    (name: keyof UpdateOrganization, inputProperty: keyof HTMLInputElement) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setUpdatedValues({ ...updatedValues, [name]: event.target[inputProperty] });
    };

  const handleClick = (id: number) => {
    setSelected(id);
    const organization = organizations.find((o) => o.id === id);
    setUpdatedValues({
      name: organization?.name ?? '',
      noHardwareMaintenance: organization?.noHardwareMaintenance ?? false,
    });
  };

  function deactivateEditMode() {
    setSelected(-1);
  }

  const isSelected = (id: number) => selected === id;

  useEffect(() => {
    setOrganizations(data.organizations);
  }, [data.organizations]);

  return (
    <Paper className={classes.paper}>
      <div className={classes.tableWrapper}>
        <Table
          className={classes.table}
          aria-labelledby="tableTitle"
          size="small"
          aria-label="enhanced table"
        >
          <SettingsTableHeader columns={organizationColumns} />
          {organizations.length === 0 ? (
            <TableRow>
              <TableCell align="center">
                <TextField
                  id="standard-basic"
                  name="name"
                  value={values.name}
                  onChange={handleCreateChange('name', 'value')}
                  style={{ width: '20vw' }}
                  multiline
                />
              </TableCell>
              <TableCell align="center">
                <Checkbox
                  name="noHardwareMaintenance"
                  checked={values.noHardwareMaintenance}
                  style={checkboxStyle}
                  onChange={handleCreateChange('noHardwareMaintenance', 'checked')}
                />
              </TableCell>
              <TableCell align="center">
                <Button
                  className={classes.button}
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleSubmit}
                >
                  Add New
                </Button>
              </TableCell>
            </TableRow>
          ) : (
            <TableBody>
              <TableRow>
                <TableCell align="center">
                  <TextField
                    id="standard-basic"
                    name="name"
                    value={values.name}
                    onChange={handleCreateChange('name', 'value')}
                    style={{ width: '20vw' }}
                    multiline
                  />
                </TableCell>
                <TableCell align="center">
                  <Checkbox
                    name="noHardwareMaintenance"
                    checked={values.noHardwareMaintenance}
                    style={checkboxStyle}
                    onChange={handleCreateChange('noHardwareMaintenance', 'checked')}
                  />
                </TableCell>
                <TableCell align="center">
                  <Button
                    className={classes.button}
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleSubmit}
                  >
                    Add New
                  </Button>
                </TableCell>
              </TableRow>
              {organizations.map((d) => {
                const isItemSelected = isSelected(d.id);

                return (
                  <TableRow
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={d.id}
                  >
                    {isItemSelected ? (
                      <TableCell align="center">
                        <TextField
                          id="standard-basic"
                          name="name"
                          defaultValue={updatedValues.name}
                          style={{ width: '20vw' }}
                          onChange={handleUpdateChange('name', 'value')}
                          multiline
                        />
                      </TableCell>
                    ) : (
                      <TableCell align="center">{d.name}</TableCell>
                    )}
                    <TableCell align="center">
                      <Checkbox
                        name="noHardwareMaintenance"
                        checked={
                          isItemSelected
                            ? updatedValues.noHardwareMaintenance
                            : d.noHardwareMaintenance
                        }
                        style={checkboxStyle}
                        disabled={!isItemSelected}
                        onChange={handleUpdateChange('noHardwareMaintenance', 'checked')}
                      />
                    </TableCell>
                    {isItemSelected ? (
                      <TableCell align="right">
                        <Tooltip title="save changes">
                          <IconButton
                            onClick={() => handleUpdate(d.id)}
                            className={classes.showDetailsIconButton}
                          >
                            <CheckIcon
                              fontSize="small"
                              className={classes.showDetailsIcon}
                            />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="cancel">
                          <IconButton
                            onClick={() => deactivateEditMode()}
                            className={classes.showDetailsIconButton}
                          >
                            <ClearIcon
                              fontSize="small"
                              className={classes.showDetailsIcon}
                            />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    ) : (
                      <TableCell align="right">
                        <Tooltip title="edit">
                          <IconButton
                            onClick={(event) => handleClick(d.id)}
                            className={classes.showDetailsIconButton}
                            disabled={selected !== -1}
                          >
                            <EditIcon
                              fontSize="small"
                              className={classes.showDetailsIcon}
                            />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="delete">
                          <IconButton
                            className={classes.showDetailsIconButton}
                            onClick={() => handleDelete(d.id)}
                          >
                            <DeleteIcon
                              fontSize="small"
                              className={classes.showDetailsIcon}
                            />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          )}
        </Table>
      </div>
    </Paper>
  );
};

export default OrganizationsList;
